import React, { useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useApi } from "../apiHelpers";
import { DataTable } from "../components/table/DataTable";
import { buttonClass } from "../components/ui/Button";
import { SearchField } from "../components/ui/SearchField";
import { Select } from "../components/ui/Select";
import { Tag } from "../components/ui/Tag";
import { CATEGORY } from "../components/ui/categoryColors";
import { Tooltip } from "../components/ui/Tooltip";
import { Alert } from "../components/ui/Alert";
import Reveal from "../components/ui/Reveal";
import { EmptyState } from "../components/ui/EmptyState";
import { LogoSpinner } from "../components/ui/LogoSpinner";
import { Panel, StatTile } from "../components/ui/Panel";
import { businessToday, formatEventDate, formatTimestamp, parseDate } from "../utils/date-utils";

// Status colours as Tag data: each status keeps its own hue.
const statusHex = {
    pending_review: CATEGORY.yellow.ink.hex,
    approved: CATEGORY.blue.ink.hex,
    partially_approved: CATEGORY.orange.ink.hex,
    awaiting_receipt: CATEGORY.purple.ink.hex,
    received: CATEGORY.indigo.ink.hex,
    in_repair: CATEGORY.orange.ink.hex,
    quality_check: CATEGORY.cyan.ink.hex,
    shipped: CATEGORY.teal.ink.hex,
    completed: CATEGORY.green.ink.hex,
    rejected: CATEGORY.red.ink.hex,
    on_hold: CATEGORY.amber.ink.hex,
    cancelled: CATEGORY.neutral.ink.hex
};

// Two formatters, because this list carries two kinds of date and one
// formatter cannot be honest about both (see web/CLAUDE.md, Date handling).
//
// estimated_completion_date is a DATE column, served as "YYYY-MM-DD": parsed
// with new Date() it became UTC midnight, which west of Greenwich renders as
// the day BEFORE the one somebody typed. parseDate/formatEventDate keep it in
// UTC end to end, so the day is the day.
const formatDay = (dateStr) => (dateStr ? formatEventDate(dateStr) : "-");

// created_at and updated_at are timestamptz: real instants, so they belong in
// the reader's own timezone. formatTimestamp is the local-mode one.
const formatStamp = (dateStr) => (dateStr ? formatTimestamp(dateStr) : "-");

const STATUS_OPTIONS = [
    { key: "", label: "All Statuses" },
    { key: "pending_review", label: "Pending Review" },
    { key: "partially_approved", label: "Partially Approved" },
    { key: "approved", label: "Approved" },
    { key: "awaiting_receipt", label: "Awaiting Receipt" },
    { key: "received", label: "Received" },
    { key: "in_repair", label: "In Repair" },
    { key: "quality_check", label: "Quality Check" },
    { key: "shipped", label: "Shipped" },
    { key: "completed", label: "Completed" },
    { key: "rejected", label: "Rejected" },
    { key: "on_hold", label: "On Hold" },
];

const formatStatus = (status) => {
    if (!status) return "-";
    return status.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
};

// Sort-typed date: a number, or undefined for empty. Never null — null makes
// the comparator inconsistent, while undefined lands on the house behavior
// (empties last ascending, first descending). Numbers because created_at and
// updated_at arrive as timestamps and estimated_completion_date as a
// YYYY-MM-DD string, and one comparator has to order both. Millis come off
// parseDate rather than new Date so no raw date parsing is left in the file;
// for an instant the two agree, and for a date-only string parseDate is the
// one that is documented to hold the day still.
const dateKey = (value) => {
    const d = parseDate(value);
    return d ? d.valueOf() : undefined;
};

// ---------------------------------------------------------------------------
// Columns for the shared DataTable.
//
// Module scope per the DataTable contract: the array never changes identity,
// so no cell subtree remounts. Every column carries an explicit id and an
// accessorFn returning the sort-typed value, because every header on this page
// sorts — the whole RMA pile is in state (see the fetch below), so a header
// click orders all of it and not one page of it.
//
// The row click is a property of the ROW, not of any column, so it lives in
// `rowProps` on the page below.
//
// Plain JavaScript file: object literals, and <DataTable> with no type
// argument. The generic exists for the .tsx consumers and costs this file
// nothing.
// ---------------------------------------------------------------------------

const RMA_COLUMNS = [
    {
        id: "rma_number",
        accessorFn: (r) => r.rma_number ?? undefined,
        header: "RMA #",
        // An identifier never wraps: a broken RMA number reads as two.
        meta: { cellClass: "font-mono text-primary whitespace-nowrap" },
        cell: ({ row }) => row.original.rma_number,
    },
    {
        id: "customer_name",
        // Sorts by customer_name, which is what this column has always sorted
        // by, even though the cell shows the organization first when there is
        // one.
        accessorFn: (r) => r.customer_name ?? undefined,
        header: "Customer",
        cell: ({ row }) => {
            const org = row.original.organization;
            const name = row.original.customer_name;
            // Prioritize organization, show customer name as secondary
            return (
                <div>
                    <div>{org || name}</div>
                    {org && (
                        <div className="text-on-surface-variant text-xs">{name}</div>
                    )}
                </div>
            );
        },
    },
    {
        id: "total_items",
        // The view's item counts are bigints, so they arrive as strings.
        // Number() so the house comparator sorts them numerically instead of
        // by collation; the == null guard keeps a real zero out of the empties
        // bucket.
        accessorFn: (r) => (r.total_items == null ? undefined : Number(r.total_items)),
        header: "Items",
        cell: ({ row }) => {
            const total = row.original.total_items || 0;
            const pending = row.original.pending_items || 0;
            const approved = row.original.approved_items || 0;
            const rejected = row.original.rejected_items || 0;

            return (
                <div className="flex items-center gap-1">
                    <span className="font-medium">{total}</span>
                    {total > 0 && (pending > 0 || approved > 0 || rejected > 0) && (
                        <span className="text-xs text-on-surface-variant">
                            ({approved > 0 && <span className="text-green-400">{approved}✓</span>}
                            {rejected > 0 && <span className="text-red-400 ml-1">{rejected}✗</span>}
                            {pending > 0 && <span className="text-yellow-400 ml-1">{pending}?</span>})
                        </span>
                    )}
                </div>
            );
        },
    },
    {
        id: "derived_status",
        accessorFn: (r) => r.derived_status ?? undefined,
        header: "Status",
        cell: ({ row }) => (
            <Tag hex={statusHex[row.original.derived_status]}>
                {formatStatus(row.original.derived_status)}
            </Tag>
        ),
    },
    {
        id: "estimated_completion_date",
        accessorFn: (r) => dateKey(r.estimated_completion_date),
        header: "Est. Completion",
        cell: ({ row }) => {
            const date = row.original.estimated_completion_date;
            if (!date) return <span className="text-on-surface-variant">-</span>;
            // Overdue is still "due before today", now measured against the day
            // on the warehouse floor. Compared against `new Date()` an RMA due
            // today read overdue from 8PM Eastern the evening BEFORE, because
            // UTC midnight had already passed.
            const isOverdue = parseDate(date).isBefore(businessToday());
            return (
                <span className={isOverdue ? "text-red-400" : "text-white"}>
                    {formatDay(date)}
                </span>
            );
        },
    },
    {
        id: "created_at",
        accessorFn: (r) => dateKey(r.created_at),
        header: "Created",
        cell: ({ row }) => formatStamp(row.original.created_at),
    },
    {
        id: "updated_at",
        accessorFn: (r) => dateKey(r.updated_at),
        header: "Updated",
        cell: ({ row }) => formatStamp(row.original.updated_at),
    },
];

// Newest first, as this list has always opened. Module scope so the
// component's dev-time check over it does not walk a fresh array every render.
const INITIAL_SORTING = [{ id: "created_at", desc: true }];

const RMAList = () => {
    const history = useHistory();
    const api = useApi();

    const [rmas, setRmas] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Filters
    const [statusFilter, setStatusFilter] = useState("");
    const [searchTerm, setSearchTerm] = useState("");

    // Fetch data
    useEffect(() => {
        fetchRMAs();
        fetchStats();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchRMAs = async () => {
        try {
            const data = await api.get("/rma");
            setRmas(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const data = await api.get("/rma/stats");
            setStats(data);
        } catch (err) {
            console.error("Failed to fetch stats:", err);
        }
    };

    // Filter data - now uses derived_status
    const filteredData = useMemo(() => {
        let data = [...rmas];

        if (statusFilter) {
            data = data.filter(r => r.derived_status === statusFilter);
        }

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            data = data.filter(r =>
                r.rma_number.toLowerCase().includes(term) ||
                r.customer_name.toLowerCase().includes(term) ||
                r.customer_email.toLowerCase().includes(term) ||
                (r.organization && r.organization.toLowerCase().includes(term))
            );
        }

        return data;
    }, [rmas, statusFilter, searchTerm]);

    if (loading) {
        return <LogoSpinner label="Loading RMA requests" />;
    }

    return (
        <div className="p-4" style={{ fontFamily: "RobotoMono, sans-serif" }}>
            {/* Stats Cards */}
            {stats && (
                <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-7">
                    <Tooltip label="Show every request">
                        <StatTile
                            label="Total"
                            value={stats.total || 0}
                            tone="neutral"
                            onClick={() => setStatusFilter("")}
                            pressed={statusFilter === ""}
                        />
                    </Tooltip>
                    <Tooltip label="Show requests pending review">
                        <StatTile
                            label="Pending"
                            value={stats.pending_review || 0}
                            tone="warn"
                            onClick={() => setStatusFilter(statusFilter === "pending_review" ? "" : "pending_review")}
                            pressed={statusFilter === "pending_review"}
                        />
                    </Tooltip>
                    <Tooltip label="Show partly approved requests">
                        <StatTile
                            label="Partial"
                            value={stats.partially_approved || 0}
                            tone="warn"
                            onClick={() => setStatusFilter(statusFilter === "partially_approved" ? "" : "partially_approved")}
                            pressed={statusFilter === "partially_approved"}
                        />
                    </Tooltip>
                    <Tooltip label="Show requests awaiting receipt">
                        <StatTile
                            label="Awaiting"
                            value={stats.awaiting_receipt || 0}
                            tone="neutral"
                            onClick={() => setStatusFilter(statusFilter === "awaiting_receipt" ? "" : "awaiting_receipt")}
                            pressed={statusFilter === "awaiting_receipt"}
                        />
                    </Tooltip>
                    <Tooltip label="Show requests in repair">
                        <StatTile
                            label="In repair"
                            value={stats.in_repair || 0}
                            tone="accent"
                            onClick={() => setStatusFilter(statusFilter === "in_repair" ? "" : "in_repair")}
                            pressed={statusFilter === "in_repair"}
                        />
                    </Tooltip>
                    <Tooltip label="Show shipped requests">
                        <StatTile
                            label="Shipped"
                            value={stats.shipped || 0}
                            tone="accent"
                            onClick={() => setStatusFilter(statusFilter === "shipped" ? "" : "shipped")}
                            pressed={statusFilter === "shipped"}
                        />
                    </Tooltip>
                    <Tooltip label="Show completed requests">
                        <StatTile
                            label="Completed"
                            value={stats.completed || 0}
                            tone="good"
                            onClick={() => setStatusFilter(statusFilter === "completed" ? "" : "completed")}
                            pressed={statusFilter === "completed"}
                        />
                    </Tooltip>
                </div>
            )}

            {/* Filters */}
            <Panel label="Filters" className="mb-6">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1">
                        <SearchField
                            label="Search RMAs"
                            placeholder="Search RMA#, organization, customer..."
                            value={searchTerm}
                            onChange={setSearchTerm}
                        />
                    </div>
                    <div>
                        <Select
                            label="Status"
                            className="md:w-48"
                            value={statusFilter}
                            onChange={setStatusFilter}
                            options={STATUS_OPTIONS}
                        />
                    </div>
                    <button
                        onClick={() => { setSearchTerm(""); setStatusFilter(""); }}
                        className={buttonClass('tonal')}
                    >
                        Clear
                    </button>
                </div>
            </Panel>

            <Reveal show={!!error}><Alert className="mb-4">{error}</Alert></Reveal>

            {/* The table. No box around it: the shared table renders its own
                vessel — shell, radius, scroll container and the face on every
                row — so a card here would draw a second one around the real
                one.

                No client pagination any more. `/rma` hands back the whole
                rma_status_view unbounded, one row per RMA REQUEST (not per
                item), and a request is a customer raising a warranty return by
                hand — so the pile grows at human pace and the whole of it is in
                state already. Paging it was furniture. A `stickyHeader` vessel
                scrolls the lot with the header band held, and the result count
                moved onto the strip, where the pagination row's "of N results"
                used to live. If that endpoint ever grows a limit/offset, this
                goes back to the MaintLogPage shape: page-owned slice, page-owned
                order, no `sortable`. */}
            <DataTable
                data={filteredData}
                columns={RMA_COLUMNS}
                getRowId={(r) => String(r.id)}
                sortable
                initialSorting={INITIAL_SORTING}
                stickyHeader
                strip={
                    <span className="ml-auto text-sm text-on-surface-variant tabular-nums" role="status" aria-live="polite">
                        {filteredData.length} result{filteredData.length === 1 ? "" : "s"}
                    </span>
                }
                rowProps={(r) => ({
                    onClick: () => history.push(`/rma/${r.rma_number}`),
                    className: "cursor-pointer",
                })}
                empty={<EmptyState variant="area">No RMA requests found</EmptyState>}
            />
        </div>
    );
};

export default RMAList;
